import { useEffect, useMemo, useState } from "react";
import client from "../api/client";

const today = () => new Date().toISOString().split("T")[0];

const getVal = (x, ...keys) => {
  for (const k of keys) {
    if (x?.[k] !== undefined && x?.[k] !== null) return x[k];
  }
  return "";
};

const safeText = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (v?.message) return String(v.message);
  if (v?.title) return String(v.title);
  try {
    return JSON.stringify(v);
  } catch {
    return "Unexpected error";
  }
};

const getErrorMessage = (e, fallback) => {
  const data = e?.response?.data;
  if (typeof data === "string") return data;
  if (data?.message) return data.message;
  if (data?.title) return data.title;
  return fallback;
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (v) =>
  num(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const styles = {
  page: { padding: 0, fontSize: 11 },
  card: {
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    overflow: "hidden",
  },
  header: {
    background: "linear-gradient(90deg,#0b4f4a,#0f766e)",
    color: "#fff",
    padding: "9px 12px",
  },
  title: { fontSize: 13, fontWeight: 700 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3, color: "#495057" },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 28, fontSize: 11, borderRadius: 7, fontWeight: 600 },
  stat: {
    borderRadius: 10,
    border: "1px solid #e9ecef",
    background: "#f8f9fa",
    padding: 9,
    height: "100%",
  },
  tableWrap: {
    overflowX: "auto",
    maxHeight: 430,
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, marginBottom: 0, minWidth: 1350 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    color: "#fff",
    background: "#07111f",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  td: {
    fontSize: 11,
    padding: "6px 8px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  badge: { fontSize: 10, padding: "4px 7px", borderRadius: 20 },
};

export default function BankReconciliationPage() {
  const [accounts, setAccounts] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);

  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const companyId = Number(localStorage.getItem("companyId") || 0);

  const [filter, setFilter] = useState({
    accountId: "",
    fromDate: today(),
    toDate: today(),
    status: "false",
    statementBalance: "",
  });

  useEffect(() => {
    loadAccounts();
  }, []);

  const bankAccounts = useMemo(() => {
    return accounts.filter((a) => {
      const name = String(getVal(a, "AccountName", "accountName")).toLowerCase();
      const code = String(getVal(a, "AccountCode", "accountCode"));
      const type = String(getVal(a, "AccountType", "accountType")).toLowerCase();

      return (
        name.includes("bank") ||
        name.includes("upi") ||
        code === "1200" ||
        type === "bank"
      );
    });
  }, [accounts]);

  const tableTotals = useMemo(() => {
    const debit = rows.reduce((s, r) => s + num(getVal(r, "Debit", "debit")), 0);
    const credit = rows.reduce((s, r) => s + num(getVal(r, "Credit", "credit")), 0);
    const reconciled = rows.filter((r) =>
      Boolean(getVal(r, "IsReconciled", "isReconciled"))
    ).length;

    return {
      debit,
      credit,
      net: debit - credit,
      reconciled,
      pending: rows.length - reconciled,
    };
  }, [rows]);

  const loadAccounts = async () => {
    try {
      setLoadingAccounts(true);
      setError("");

      const res = await client.get("/accounts");
      setAccounts(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(getErrorMessage(e, "Failed to load bank accounts"));
    } finally {
      setLoadingAccounts(false);
    }
  };

  const buildParams = () => {
    const params = {
      accountId: Number(filter.accountId),
      fromDate: filter.fromDate,
      toDate: filter.toDate,
    };

    if (filter.status !== "all") {
      params.isReconciled = filter.status === "true";
    }

    if (companyId > 0) {
      params.companyId = companyId;
    }

    return params;
  };

  const loadEntries = async () => {
    if (!filter.accountId) return alert("Select bank account");
    if (!filter.fromDate || !filter.toDate) return alert("Select date range");

    try {
      setLoadingRows(true);
      setError("");

      const params = buildParams();

      const [entryRes, summaryRes] = await Promise.all([
        client.get("/finance/bank-reconciliation", { params }),
        client.get("/finance/bank-reconciliation/summary", {
          params: {
            accountId: Number(filter.accountId),
            fromDate: filter.fromDate,
            toDate: filter.toDate,
            statementBalance: num(filter.statementBalance),
            companyId: companyId > 0 ? companyId : undefined,
          },
        }),
      ]);

      setRows(Array.isArray(entryRes.data) ? entryRes.data : []);
      setSummary(summaryRes.data && typeof summaryRes.data === "object" ? summaryRes.data : null);
    } catch (e) {
      setError(getErrorMessage(e, "Failed to load reconciliation entries"));
    } finally {
      setLoadingRows(false);
    }
  };

  const reconcile = async (row) => {
    const journalDetailId = getVal(row, "JournalDetailId", "journalDetailId");
    if (!journalDetailId) return alert("Journal detail id missing");

    const bankReferenceNo = prompt("Enter Bank Reference No / UTR", "");
    if (bankReferenceNo === null) return;

    const remarks = prompt("Enter remarks, optional", "");
    if (remarks === null) return;

    try {
      setSaving(true);

      await client.post("/finance/bank-reconciliation/reconcile", {
        journalDetailId: Number(journalDetailId),
        reconciledDate: today(),
        bankReferenceNo: bankReferenceNo.trim(),
        remarks: remarks.trim(),
      });

      await loadEntries();
    } catch (e) {
      alert(getErrorMessage(e, "Failed to reconcile entry"));
    } finally {
      setSaving(false);
    }
  };

  const unreconcile = async (row) => {
    const journalDetailId = getVal(row, "JournalDetailId", "journalDetailId");
    if (!journalDetailId) return alert("Journal detail id missing");

    if (!window.confirm("Undo reconciliation for this entry?")) return;

    try {
      setSaving(true);

      await client.post(
        `/finance/bank-reconciliation/unreconcile/${journalDetailId}`
      );

      await loadEntries();
    } catch (e) {
      alert(getErrorMessage(e, "Failed to undo reconciliation"));
    } finally {
      setSaving(false);
    }
  };

  const difference = num(getVal(summary, "Difference", "difference"));

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>
            <i className="bi bi-check2-square me-2"></i>
            Bank Reconciliation
          </div>
          <div style={styles.sub}>
            Match bank transactions with ERP ledger entries
          </div>
        </div>

        <div style={styles.body}>
          {error && (
            <div className="alert alert-danger py-2 mb-2" style={{ fontSize: 11 }}>
              {safeText(error)}
            </div>
          )}

          <div className="row g-2 mb-2">
            <Field label="Bank Account" className="col-md-3">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={filter.accountId}
                onChange={(e) =>
                  setFilter({ ...filter, accountId: e.target.value })
                }
                disabled={loadingAccounts}
              >
                <option value="">
                  {loadingAccounts ? "Loading banks..." : "Select Bank"}
                </option>

                {bankAccounts.map((a) => {
                  const id = getVal(a, "AccountId", "accountId", "Id", "id");
                  const code = getVal(a, "AccountCode", "accountCode");
                  const name = getVal(a, "AccountName", "accountName");

                  return (
                    <option key={id} value={id}>
                      {code ? `${safeText(code)} - ${safeText(name)}` : safeText(name)}
                    </option>
                  );
                })}
              </select>
            </Field>

            <Field label="From" className="col-md-2">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={filter.fromDate}
                onChange={(e) =>
                  setFilter({ ...filter, fromDate: e.target.value })
                }
              />
            </Field>

            <Field label="To" className="col-md-2">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={filter.toDate}
                onChange={(e) =>
                  setFilter({ ...filter, toDate: e.target.value })
                }
              />
            </Field>

            <Field label="Status" className="col-md-2">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={filter.status}
                onChange={(e) =>
                  setFilter({ ...filter, status: e.target.value })
                }
              >
                <option value="false">Unreconciled</option>
                <option value="true">Reconciled</option>
                <option value="all">All</option>
              </select>
            </Field>

            <Field label="Statement Balance" className="col-md-2">
              <input
                type="number"
                step="0.01"
                className="form-control form-control-sm"
                style={styles.input}
                value={filter.statementBalance}
                onChange={(e) =>
                  setFilter({ ...filter, statementBalance: e.target.value })
                }
                placeholder="Bank closing balance"
              />
            </Field>

            <div className="col-md-1 d-flex align-items-end">
              <button
                className="btn btn-primary btn-sm w-100"
                style={styles.btn}
                onClick={loadEntries}
                disabled={loadingRows}
              >
                {loadingRows ? "..." : "Load"}
              </button>
            </div>
          </div>

          <div className="row g-2 mb-2">
            <Stat title="Entries" value={rows.length} color="#0d6efd" />
            <Stat title="Pending" value={tableTotals.pending} color="#ffc107" />
            <Stat title="Reconciled" value={tableTotals.reconciled} color="#0f766e" />
            <Stat title="Debit" value={`₹ ${money(tableTotals.debit)}`} color="#0f766e" />
            <Stat title="Credit" value={`₹ ${money(tableTotals.credit)}`} color="#dc3545" />
            <Stat
              title="Net"
              value={`₹ ${money(Math.abs(tableTotals.net))} ${
                tableTotals.net >= 0 ? "Dr" : "Cr"
              }`}
              color={tableTotals.net >= 0 ? "#0f766e" : "#dc3545"}
            />
          </div>

          <div className="row g-2 mb-2">
            <Stat
              title="Opening Balance"
              value={`₹ ${money(getVal(summary, "OpeningBalance", "openingBalance"))}`}
              color="#6f42c1"
            />
            <Stat
              title="ERP Closing"
              value={`₹ ${money(getVal(summary, "ErpClosingBalance", "erpClosingBalance"))}`}
              color="#0d6efd"
            />
            <Stat
              title="Statement Balance"
              value={`₹ ${money(getVal(summary, "StatementBalance", "statementBalance"))}`}
              color="#198754"
            />
            <Stat
              title="Difference"
              value={`₹ ${money(Math.abs(difference))}`}
              color={difference === 0 ? "#198754" : "#dc3545"}
            />
            <Stat
              title="Unrec. Debit"
              value={`₹ ${money(getVal(summary, "UnreconciledDebit", "unreconciledDebit"))}`}
              color="#fd7e14"
            />
            <Stat
              title="Unrec. Credit"
              value={`₹ ${money(getVal(summary, "UnreconciledCredit", "unreconciledCredit"))}`}
              color="#fd7e14"
            />
          </div>

          <div
            style={{
              fontSize: 10,
              color: difference === 0 ? "#198754" : "#dc3545",
              marginBottom: 6,
              fontWeight: 700,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span>
              {summary
                ? difference === 0
                  ? "Bank reconciliation matched."
                  : "Bank reconciliation difference exists."
                : "Load entries to view reconciliation summary."}
            </span>
            <span>
              Company ID: <strong>{companyId || "All"}</strong>
            </span>
          </div>

          <div style={styles.tableWrap}>
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  {[
                    "Date",
                    "Voucher No",
                    "Voucher Type",
                    "Description",
                    "Debit",
                    "Credit",
                    "Amount",
                    "Status",
                    "Bank Ref",
                    "Remarks",
                    "Reconciled Date",
                    "Action",
                  ].map((h) => (
                    <th
                      key={h}
                      style={styles.th}
                      className={
                        ["Debit", "Credit", "Amount"].includes(h)
                          ? "text-end"
                          : ""
                      }
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="12"
                      className="text-center text-muted"
                      style={{ ...styles.td, padding: 18 }}
                    >
                      {loadingRows ? "Loading entries..." : "No entries"}
                    </td>
                  </tr>
                ) : (
                  rows.map((r, i) => {
                    const isRec = Boolean(getVal(r, "IsReconciled", "isReconciled"));
                    const entryDate = getVal(r, "EntryDate", "entryDate");
                    const recDate = getVal(r, "ReconciledDate", "reconciledDate");

                    const debit = num(getVal(r, "Debit", "debit"));
                    const credit = num(getVal(r, "Credit", "credit"));
                    const amount =
                      num(getVal(r, "Amount", "amount")) ||
                      (debit > 0 ? debit : credit);

                    return (
                      <tr key={getVal(r, "JournalDetailId", "journalDetailId") || i}>
                        <td style={styles.td}>
                          {entryDate
                            ? new Date(entryDate).toLocaleDateString("en-IN")
                            : ""}
                        </td>

                        <td style={{ ...styles.td, fontWeight: 700 }}>
                          {safeText(getVal(r, "VoucherNo", "voucherNo"))}
                        </td>

                        <td style={styles.td}>
                          {safeText(getVal(r, "VoucherType", "voucherType") || "-")}
                        </td>

                        <td style={styles.td}>
                          {safeText(getVal(r, "Description", "description"))}
                        </td>

                        <td className="text-end" style={styles.td}>
                          {debit > 0 ? money(debit) : ""}
                        </td>

                        <td className="text-end" style={styles.td}>
                          {credit > 0 ? money(credit) : ""}
                        </td>

                        <td className="text-end fw-bold" style={styles.td}>
                          {money(amount)}
                        </td>

                        <td style={styles.td}>
                          <span
                            className={`badge ${
                              isRec ? "text-bg-success" : "text-bg-warning"
                            }`}
                            style={styles.badge}
                          >
                            {isRec ? "Reconciled" : "Pending"}
                          </span>
                        </td>

                        <td style={styles.td}>
                          {safeText(getVal(r, "BankReferenceNo", "bankReferenceNo") || "-")}
                        </td>

                        <td style={styles.td}>
                          {safeText(
                            getVal(
                              r,
                              "BankReconciliationRemarks",
                              "bankReconciliationRemarks"
                            ) || "-"
                          )}
                        </td>

                        <td style={styles.td}>
                          {recDate
                            ? new Date(recDate).toLocaleDateString("en-IN")
                            : "-"}
                        </td>

                        <td style={styles.td}>
                          {isRec ? (
                            <button
                              className="btn btn-outline-danger btn-sm"
                              style={styles.btn}
                              disabled={saving}
                              onClick={() => unreconcile(r)}
                            >
                              Undo
                            </button>
                          ) : (
                            <button
                              className="btn btn-success btn-sm"
                              style={styles.btn}
                              disabled={saving}
                              onClick={() => reconcile(r)}
                            >
                              Reconcile
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, className, children }) {
  return (
    <div className={className}>
      <label style={styles.label}>{safeText(label)}</label>
      {children}
    </div>
  );
}

function Stat({ title, value, color }) {
  return (
    <div className="col-md-2">
      <div style={{ ...styles.stat, borderTop: `3px solid ${color}` }}>
        <div style={{ fontSize: 10, color: "#6c757d", fontWeight: 700 }}>
          {safeText(title)}
        </div>
        <div style={{ fontSize: 14, fontWeight: 800, color }}>
          {safeText(value)}
        </div>
      </div>
    </div>
  );
}